import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PhoneInternational from '../../components/PhoneInternational';
import MinimalInput from '../../components/MinimalInput/MinimalInput';
import CustomButton from '../../components/CustomButton/CustomButton';
import LocationSelector from '../../components/LocationSelector';
import LanguageSelector from '../../components/LanguageSelector/LanguageSelector';
import SpecialtySelector from '../../components/SpecialtySelector';
import PriceSelector from '../../components/PriceSelector';
import PositionSelector from '../../components/PositionSelector';
import PasswordStrengthIndicator from '../../components/PasswordStrengthIndicator';
import { useNavigate } from 'react-router-dom';
import { registerLawyerUser } from '../../redux/slices/authSlice';
import { navigateToLawyerDashboard } from '../../redux/slices/uiSlice';
import { validatePassword } from '../../utils/authUtils';

export default function LawyerSignUp({ isLawyer = false }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading, error: authError } = useSelector(state => state.auth);

  const [lawyerData, setLawyerData] = useState({
    nombre: '',
    apellidopaterno: '',
    apellidomaterno: '',
    correo: '',
    location: {
      country: '',
      state: '',
      city: '',
    },
    fotografia: '',
    telefono: '',
    phoneData: {
      dial_code: '52',
      iso2: 'mx',
      country: 'Mexico',
    },
    password: '',
    confirmPassword: '',
    descripcion: '',
    despacho: '',
    posicion: '',
    ubicacionDespacho: '',
    cedula: '',
    fechaInicio: '',
    fechaNacimiento: '',
    educacion: {
      licenciatura: '',
      maestria: '',
      doctorado: '',
      diplomado: '',
    },
    idiomas: [], // Array para múltiples idiomas
    servicios: [],
    especialidades: [],
    pricing: {
      priceRange: '',
      hasFirstConsultation: false,
      firstConsultationPrice: null,
    },
    rfc: '',
  });
  const [touchedFields, setTouchedFields] = useState({});
  const [passwordValidation, setPasswordValidation] = useState({
    isValid: false,
    errors: [],
    strength: 0,
  });

  // Helper function to convert file to base64
  const convertToBase64 = file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handleLawyerChange = field => e => {
    const value = e.target.value;
    setLawyerData(prev => ({
      ...prev,
      [field]: value,
    }));
    setTouchedFields(prev => ({
      ...prev,
      [field]: true,
    }));

    // Validate password on change
    if (field === 'password') {
      const validation = validatePassword(value);
      setPasswordValidation(validation);
    }
  };

  const handleLawyerEduChange = field => e => {
    setLawyerData(prev => ({
      ...prev,
      educacion: {
        ...prev.educacion,
        [field]: e.target.value,
      },
    }));
  };

  const handleLocationChange = locationData => {
    setLawyerData(prev => ({
      ...prev,
      location: locationData,
    }));
  };

  const handleLanguageChange = selectedLanguages => {
    setLawyerData(prev => ({
      ...prev,
      idiomas: selectedLanguages, // Array de idiomas seleccionados
    }));
  };

  const handleSpecialtyChange = selectedSpecialties => {
    setLawyerData(prev => ({
      ...prev,
      especialidades: selectedSpecialties,
    }));
  };

  const handlePriceChange = pricingData => {
    setLawyerData(prev => ({
      ...prev,
      pricing: pricingData,
    }));
  };

  const handlePositionChange = position => {
    setLawyerData(prev => ({
      ...prev,
      posicion: position,
    }));
  };

  const handleBlur = field => () => {
    setTouchedFields(prev => ({
      ...prev,
      [field]: true,
    }));
  };

  const isFieldValid = field => {
    if (!touchedFields[field]) return true;
    return !!lawyerData[field];
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // Clear any previous auth errors
    if (authError) {
      // Clear error from Redux
      // dispatch(clearError());
    }

    // Mark all fields as touched
    const allFields = Object.keys(lawyerData);
    const newTouchedFields = allFields.reduce((acc, field) => {
      acc[field] = true;
      return acc;
    }, {});
    setTouchedFields(newTouchedFields);

    // Validate required fields
    const requiredFields = [
      'nombre',
      'apellidopaterno',
      'correo',
      'telefono',
      'password',
      'confirmPassword',
      'descripcion',
      'despacho',
      'posicion',
      'ubicacionDespacho',
      'cedula',
      'fechaInicio',
      'fechaNacimiento',
      'rfc',
    ];

    const missingFields = requiredFields.filter(field => !lawyerData[field]);

    if (missingFields.length > 0) {
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(lawyerData.correo)) {
      return;
    }

    // Validate password strength
    const passwordValidation = validatePassword(lawyerData.password);
    if (!passwordValidation.isValid) {
      return;
    }

    if (lawyerData.password !== lawyerData.confirmPassword) {
      return;
    }

    // Validate that at least one specialty is selected
    if (lawyerData.especialidades.length === 0) {
      return;
    }

    // Validate that a price range is selected
    if (!lawyerData.pricing.priceRange) {
      return;
    }

    try {
      // Prepare data according to backend format
      const mainData = {
        lawyer_email: lawyerData.correo,
        lawyer_password: lawyerData.password,
        lawyer_name: lawyerData.nombre,
        lawyer_appaterno: lawyerData.apellidopaterno,
        lawyer_apmaterno: lawyerData.apellidomaterno || null,
        lawyer_working_begin_date: lawyerData.fechaInicio,
        lawyer_birthday: lawyerData.fechaNacimiento,
        lawyer_photograph: lawyerData.fotografia || null, // Base64 image
        lawyer_status: 1,
        lawyer_matricula: lawyerData.cedula,
        lawyer_begin: new Date(),
        lawyer_services: lawyerData.servicios.includes('inscrito') ? 2 : 1,
        lawyer_description: lawyerData.descripcion,
        lawyer_rfc: lawyerData.rfc,
        lawyer_state: null,
        lawyer_country: lawyerData.location.country || null,
        lawyer_estado: lawyerData.location.state || null,
        lawyer_city: lawyerData.location.city || null,
        lawyer_status_legaltek: 1,
        id_lawyer_costo: lawyerData.pricing.priceRange,
        lawyer_first_charge: lawyerData.pricing.hasFirstConsultation
          ? lawyerData.pricing.firstConsultationPrice
          : null,
        lawyer_phone: lawyerData.telefono,
        dTimeStamp: new Date(),
      };

      // Prepare academic degrees
      const academicDegrees = Object.entries(lawyerData.educacion)
        .filter(([key, value]) => value && value.trim() !== '')
        .map(([key, value]) => ({
          type: key,
          institution: value.trim(),
        }));

      const fullData = {
        main: mainData,
        espec: lawyerData.especialidades,
        languages: lawyerData.idiomas ? [lawyerData.idiomas] : [], // Convert string to array for backend
        state: lawyerData.location,
        academic_degrees: academicDegrees,
        dTimeStamp: new Date(),
      };

      // Dispatch registration action
      await dispatch(registerLawyerUser(fullData)).unwrap();

      // Usar Redux para manejar la navegación
      dispatch(navigateToLawyerDashboard());
      // Redirect to dashboard on success
      navigate('/lawyer-dashboard');
    } catch (error) {
      // Error handling is managed by Redux
      console.error('Registration error:', error);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-4 px-4 md:px-0"
    >
      <MinimalInput
        label="Nombre"
        name="nombre"
        value={lawyerData.nombre}
        onChange={handleLawyerChange('nombre')}
        onBlur={handleBlur('nombre')}
        placeholder="Tu nombre"
        className={`rounded-md px-4 py-3 text-base transition-colors duration-200 ${
          isLawyer
            ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
            : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
        } ${!isFieldValid('nombre') ? '!border-red-500 focus:!border-red-500' : ''}`}
        required
      />
      <MinimalInput
        label="Apellido Paterno"
        name="apellidopaterno"
        value={lawyerData.apellidopaterno}
        onChange={handleLawyerChange('apellidopaterno')}
        onBlur={handleBlur('apellidopaterno')}
        placeholder="Apellido paterno"
        className={`rounded-md px-4 py-3 text-base transition-colors duration-200 ${
          isLawyer
            ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
            : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
        } ${!isFieldValid('apellidopaterno') ? '!border-red-500 focus:!border-red-500' : ''}`}
        required
      />
      <MinimalInput
        label="Apellido Materno"
        name="apellidomaterno"
        value={lawyerData.apellidomaterno}
        onChange={handleLawyerChange('apellidomaterno')}
        onBlur={handleBlur('apellidomaterno')}
        placeholder="Apellido materno"
        className={`rounded-md px-4 py-3 text-base transition-colors duration-200 ${
          isLawyer
            ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
            : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
        } ${!isFieldValid('apellidomaterno') ? '!border-red-500 focus:!border-red-500' : ''}`}
        required
      />
      <MinimalInput
        label="Correo Electrónico"
        name="correo"
        type="email"
        value={lawyerData.correo}
        onChange={handleLawyerChange('correo')}
        onBlur={handleBlur('correo')}
        placeholder="ejemplo@correo.com"
        className={`rounded-md px-4 py-3 text-base transition-colors duration-200 ${
          isLawyer
            ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
            : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
        } ${!isFieldValid('correo') ? '!border-red-500 focus:!border-red-500' : ''}`}
        required
      />

      {/* Ubicación usando LocationSelector */}
      <div className="col-span-1 md:col-span-2 mb-6">
        <LocationSelector onLocationChange={handleLocationChange} isLawyer={isLawyer} />
      </div>

      {/* Fotografía y subir foto */}
      <div className="flex flex-col w-full mb-6">
        <label className={`text-xs mb-1 ${isLawyer ? 'text-gray-300' : 'text-gray-500'}`}>
          Fotografía
        </label>
        <input
          type="file"
          accept="image/*"
          className={`text-xs ${!isFieldValid('fotografia') ? 'border-red-500' : ''}`}
          onChange={async e => {
            const file = e.target.files[0];
            if (file) {
              try {
                const base64 = await convertToBase64(file);
                setLawyerData(prev => ({ ...prev, fotografia: base64 }));
                setTouchedFields(prev => ({ ...prev, fotografia: true }));
              } catch (error) {
                console.error('Error converting image to base64:', error);
                // Handle error through a local state or just log it
              }
            }
          }}
          onBlur={handleBlur('fotografia')}
          required
        />
      </div>
      {/* Teléfono Celular */}
      <div className="flex flex-col w-full mb-6">
        <label
          className={`text-xs mb-1 ${isLawyer ? 'text-gray-300' : 'text-gray-500'}`}
          htmlFor="telefono-abogado"
        >
          Teléfono Celular
        </label>
        <PhoneInternational
          id="telefono-abogado"
          theInput={countryData => {
            setLawyerData(prev => ({
              ...prev,
              phoneData: {
                dial_code: countryData?.dialCode || '52',
                iso2: countryData?.iso2 || 'mx',
                country: countryData?.name || 'Mexico',
              },
            }));
          }}
          thePhone={phone => {
            setLawyerData(prev => ({
              ...prev,
              telefono: phone,
            }));
          }}
          className={!isFieldValid('telefono') ? 'border-red-500' : ''}
        />
      </div>
      <MinimalInput
        label="Contraseña"
        name="password"
        type="password"
        value={lawyerData.password}
        onChange={handleLawyerChange('password')}
        onBlur={handleBlur('password')}
        placeholder="••••••••"
        className={`rounded-md px-4 py-3 text-base transition-colors duration-200 ${
          isLawyer
            ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
            : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
        } ${!isFieldValid('password') ? '!border-red-500 focus:!border-red-500' : ''}`}
        required
      />

      {/* Password Strength Indicator */}
      {lawyerData.password && (
        <div className="col-span-1 md:col-span-2 -mt-2 mb-4">
          <PasswordStrengthIndicator
            password={lawyerData.password}
            validation={passwordValidation}
          />
        </div>
      )}

      <MinimalInput
        label="Confirmar Contraseña"
        name="confirmPassword"
        type="password"
        value={lawyerData.confirmPassword}
        onChange={handleLawyerChange('confirmPassword')}
        onBlur={handleBlur('confirmPassword')}
        placeholder="••••••••"
        className={`rounded-md px-4 py-3 text-base transition-colors duration-200 ${
          isLawyer
            ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
            : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
        } ${!isFieldValid('confirmPassword') ? '!border-red-500 focus:!border-red-500' : ''}`}
        required
      />
      <MinimalInput
        label="Descripción Profesional"
        name="descripcion"
        value={lawyerData.descripcion}
        onChange={handleLawyerChange('descripcion')}
        onBlur={handleBlur('descripcion')}
        placeholder="Describe tu experiencia"
        className={`rounded-md px-4 py-3 text-base transition-colors duration-200 ${
          isLawyer
            ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
            : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
        } ${!isFieldValid('descripcion') ? '!border-red-500 focus:!border-red-500' : ''}`}
        required
      />
      <MinimalInput
        label="Despacho"
        name="despacho"
        value={lawyerData.despacho}
        onChange={handleLawyerChange('despacho')}
        onBlur={handleBlur('despacho')}
        placeholder="Nombre del despacho"
        className={`rounded-md px-4 py-3 text-base transition-colors duration-200 ${
          isLawyer
            ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
            : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
        } ${!isFieldValid('despacho') ? '!border-red-500 focus:!border-red-500' : ''}`}
        required
      />
      <div className="flex flex-col w-full mb-6">
        <label className={`text-xs mb-1 ${isLawyer ? 'text-gray-300' : 'text-gray-500'}`}>
          Posición *
        </label>
        <PositionSelector onPositionChange={handlePositionChange} isLawyer={isLawyer} />
      </div>
      <MinimalInput
        label="Ubicación Despacho"
        name="ubicacionDespacho"
        value={lawyerData.ubicacionDespacho}
        onChange={handleLawyerChange('ubicacionDespacho')}
        onBlur={handleBlur('ubicacionDespacho')}
        placeholder="Dirección del despacho"
        className={`rounded-md px-4 py-3 text-base transition-colors duration-200 ${
          isLawyer
            ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
            : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
        } ${!isFieldValid('ubicacionDespacho') ? '!border-red-500 focus:!border-red-500' : ''}`}
        required
      />
      <MinimalInput
        label="Cédula"
        name="cedula"
        value={lawyerData.cedula}
        onChange={handleLawyerChange('cedula')}
        onBlur={handleBlur('cedula')}
        placeholder="Número de cédula"
        className={`rounded-md px-4 py-3 text-base transition-colors duration-200 ${
          isLawyer
            ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
            : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
        } ${!isFieldValid('cedula') ? '!border-red-500 focus:!border-red-500' : ''}`}
        required
      />
      <MinimalInput
        label="RFC"
        name="rfc"
        value={lawyerData.rfc}
        onChange={handleLawyerChange('rfc')}
        placeholder="RFC"
        className={`rounded-md px-4 py-3 text-base transition-colors duration-200 ${
          isLawyer
            ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
            : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
        }`}
        required
      />
      {authError && (
        <div className="col-span-1 md:col-span-2 text-red-500 text-xs mb-2 text-center">
          {authError}
        </div>
      )}
      <MinimalInput
        label="Fecha de Inicio del Ejercicio Profesional"
        name="fechaInicio"
        type="date"
        value={lawyerData.fechaInicio}
        onChange={handleLawyerChange('fechaInicio')}
        onBlur={handleBlur('fechaInicio')}
        className={`rounded-md px-4 py-3 text-base transition-colors duration-200 ${
          isLawyer
            ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
            : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
        } ${!isFieldValid('fechaInicio') ? '!border-red-500 focus:!border-red-500' : ''}`}
        required
      />
      <MinimalInput
        label="Fecha de Nacimiento"
        name="fechaNacimiento"
        type="date"
        value={lawyerData.fechaNacimiento}
        onChange={handleLawyerChange('fechaNacimiento')}
        onBlur={handleBlur('fechaNacimiento')}
        className={`rounded-md px-4 py-3 text-base transition-colors duration-200 ${
          isLawyer
            ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
            : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
        } ${!isFieldValid('fechaNacimiento') ? '!border-red-500 focus:!border-red-500' : ''}`}
        required
      />
      {/* Educación */}
      <div className="col-span-1 md:col-span-2 mt-2">
        <h2 className={`text-base font-semibold mb-2 ${isLawyer ? 'text-white' : 'text-gray-700'}`}>
          Educación
        </h2>
        <MinimalInput
          label="Licenciatura"
          name="licenciatura"
          value={lawyerData.educacion.licenciatura}
          onChange={handleLawyerEduChange('licenciatura')}
          placeholder="Licenciatura"
          className={`rounded-md px-4 py-3 text-base transition-colors duration-200 ${
            isLawyer
              ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
              : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
          }`}
        />
        <MinimalInput
          label="Maestría"
          name="maestria"
          value={lawyerData.educacion.maestria}
          onChange={handleLawyerEduChange('maestria')}
          placeholder="Maestría"
          className={`rounded-md px-4 py-3 text-base transition-colors duration-200 ${
            isLawyer
              ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
              : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
          }`}
        />
        <MinimalInput
          label="Doctorado"
          name="doctorado"
          value={lawyerData.educacion.doctorado}
          onChange={handleLawyerEduChange('doctorado')}
          placeholder="Doctorado"
          className={`rounded-md px-4 py-3 text-base transition-colors duration-200 ${
            isLawyer
              ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
              : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
          }`}
        />
        <MinimalInput
          label="Diplomado"
          name="diplomado"
          value={lawyerData.educacion.diplomado}
          onChange={handleLawyerEduChange('diplomado')}
          placeholder="Diplomado"
          className={`rounded-md px-4 py-3 text-base transition-colors duration-200 ${
            isLawyer
              ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
              : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
          }`}
        />
      </div>

      {/* Idiomas */}
      <div className="col-span-1 md:col-span-2 mb-6">
        <LanguageSelector onLanguageChange={handleLanguageChange} isLawyer={isLawyer} />
      </div>

      {/* Servicios a ofrecer */}
      <div className="col-span-1 md:col-span-2 mt-8 mb-8">
        <h2
          className={`text-2xl font-serif font-semibold mb-6 ${isLawyer ? 'text-white' : 'text-gray-700'}`}
        >
          Servicios a ofrecer en Iustia
        </h2>
        <div className="flex flex-col gap-6 bg-white/5 p-6 rounded-lg">
          <div className="flex flex-col gap-3">
            <label
              className={`flex items-center gap-3 text-lg ${isLawyer ? 'text-white' : 'text-gray-900'}`}
            >
              <input
                type="checkbox"
                className="w-5 h-5"
                checked={lawyerData.servicios.includes('cita')}
                onChange={e =>
                  setLawyerData(prev => ({
                    ...prev,
                    servicios: e.target.checked
                      ? [...prev.servicios, 'cita']
                      : prev.servicios.filter(s => s !== 'cita'),
                  }))
                }
              />
              Cita
            </label>
            <span className={`text-base ml-8 ${isLawyer ? 'text-gray-300' : 'text-gray-500'}`}>
              Recibe citas de todos los que necesiten de tus servicios conforme a tu horario.
            </span>
          </div>
          <div className="flex flex-col gap-3">
            <label
              className={`flex items-center gap-3 text-lg ${isLawyer ? 'text-white' : 'text-gray-900'}`}
            >
              <input
                type="checkbox"
                className="w-5 h-5"
                checked={lawyerData.servicios.includes('inscrito')}
                onChange={e =>
                  setLawyerData(prev => ({
                    ...prev,
                    servicios: e.target.checked
                      ? [...prev.servicios, 'inscrito']
                      : prev.servicios.filter(s => s !== 'inscrito'),
                  }))
                }
              />
              Inscrito
            </label>
            <span className={`text-base ml-8 ${isLawyer ? 'text-gray-300' : 'text-gray-500'}`}>
              Ayuda a las personas a resolver sus dudas jurídicas en videollamada, cobrando por
              hora.
            </span>
          </div>
        </div>
      </div>

      {/* Especialidades */}
      <div className="col-span-1 md:col-span-2 mb-6">
        <SpecialtySelector onSpecialtyChange={handleSpecialtyChange} isLawyer={isLawyer} />
      </div>

      {/* Precios */}
      <div className="col-span-1 md:col-span-2 mb-6">
        <PriceSelector onPriceChange={handlePriceChange} isLawyer={isLawyer} />
      </div>

      <div className="col-span-1 md:col-span-2 flex justify-center mt-6">
        <CustomButton
          type="submit"
          text={isLoading ? 'Registrando...' : 'Crear Cuenta'}
          disabled={isLoading}
          className="w-full justify-center py-3 text-base"
        />
      </div>
    </form>
  );
}
